import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  getSessionId,
  getSessionIdFromUrl,
  pathWithSessionId,
  initializeSession,
  cancelCustomerProcessingKeepalive,
} from '../utils/sessionManager';
import { getApiBaseUrl } from '../utils/apiConfig';
import { saveSkinDetection } from '../utils/dataSaver';
import { detectSkinBatch, analyzeSkinMacro, requestSkinAge, requestGenerateRoutine } from '../utils/skinAnalysisApi';
import { fetchAllowedConditionNames } from '../utils/segmentation';
import { getPreviousSkinAnalysis } from '../utils/previousSkinAnalysis';
import {
  normalizeDetections,
  normalizeDescriptionMap,
  normalizeSkinScore,
} from '../utils/skinConditionNormalization';
import {
  REQUEST_STATUS,
  createRequestScope,
  isAbortError,
  isTimeoutError,
  logSalonResilience,
} from '../utils/salonResilience';
import AISkinLoader from '../components/AISkinLoader';
import SalonHeaderActions from '../components/SalonHeaderActions';
import FaceIdentityBrandLogo from '../../face-identity/components/FaceIdentityBrandLogo';
import { useFaceIdentityOrgLogo } from '../../face-identity/hooks/useFaceIdentityOrgLogo';
import { useSkinSession } from '../context/SkinSessionContext';
import { useSkinAnalysisPlan } from '../context/SkinAnalysisPlanContext';
import { goSalonHubForNewUser } from '../utils/salonHomeNav';
import { resolveGuestUserDataForAnalysis } from '../utils/bootstrapFaceSalonSession';
import './AISkinAnalysisPage.css';
import '../styles/PageBackground.css';
import '../styles/Typography.css';
import '../styles/ActionButtons.css';

const CONF_THRESHOLD_MESSAGE = 'Analysis is still running. Please wait a moment…';

function useResolvedSession(location, token) {
  const [sessionId, setSessionId] = useState(() => {
    const fromState = location.state?.sessionId;
    const fromUrl = getSessionIdFromUrl(location);
    const fromStorage = getSessionId();
    return fromState ?? fromUrl ?? fromStorage ?? null;
  });
  const apiBaseUrl = getApiBaseUrl();

  useEffect(() => {
    const fromState = location.state?.sessionId;
    const fromUrl = getSessionIdFromUrl(location);
    const fromStorage = getSessionId();
    const resolved = fromState || fromUrl || fromStorage;

    if (resolved && sessionId !== resolved) {
      setSessionId(resolved);
      return;
    }
    if (sessionId) return;

    const init = async () => {
      const sid = await initializeSession(apiBaseUrl, token ? { app_session_token: token } : {});
      setSessionId(sid);
    };
    init();
  }, [apiBaseUrl, sessionId, location, token]);

  return [sessionId, setSessionId];
}

const AISkinAnalysisPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useSkinSession();
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();
  const {
    ENABLE_OLLAMA_DESCRIPTIONS,
    ENABLE_RADAR_CHART,
    ENABLE_BEFORE_RADAR_CHART,
  } = useSkinAnalysisPlan();

  const capturedImage = location.state?.capturedImage ?? null;
  const sideCaptures = location.state?.sideCaptures ?? null;
  const userDataFromState = resolveGuestUserDataForAnalysis(location.state);
  const aqiDataFromState = location.state?.aqiData ?? null;
  const macroCapturesFromState = location.state?.macroCaptures ?? null;
  const macroSkippedFromState = Boolean(location.state?.macroSkipped);

  const [sessionId, setSessionId] = useResolvedSession(location, token);
  const [isDetecting, setIsDetecting] = useState(false);
  const [error, setError] = useState(null);
  const [requestStatus, setRequestStatus] = useState(REQUEST_STATUS.IDLE);
  const [detectProgress, setDetectProgress] = useState(null);

  const hasDetectedRef = useRef(false);
  const detectionInProgressRef = useRef(false);
  const requestScopeRef = useRef(null);
  const retryCountRef = useRef(0);
  const manualRetryBusyRef = useRef(false);
  const sessionIdRef = useRef(sessionId);
  const activeDetectRequestIdRef = useRef(null);
  const isDetectingRef = useRef(isDetecting);
  sessionIdRef.current = sessionId;
  isDetectingRef.current = isDetecting;

  const cancelServerProcessingIfNeeded = useCallback(
    (reason, ownerRequestId) => {
      if (reason === 'retry-replacement') return;
      if (!detectionInProgressRef.current && !isDetectingRef.current) return;
      const owner =
        ownerRequestId
        || activeDetectRequestIdRef.current;
      if (!owner) return;
      const sid =
        sessionIdRef.current
        ?? getSessionIdFromUrl(location)
        ?? getSessionId();
      if (!sid) return;
      cancelCustomerProcessingKeepalive(getApiBaseUrl(), sid, owner, token || null);
      logSalonResilience('processing_cancel_keepalive', { reason, sessionId: sid, ownerRequestId: owner });
    },
    [location, token]
  );

  const abortActiveRequest = useCallback((reason = 'navigation') => {
    const scope = requestScopeRef.current;
    if (scope) {
      logSalonResilience('abort', { label: 'detect-skin-page', reason, requestId: scope.requestId });
      scope.abort(reason);
      requestScopeRef.current = null;
    }
    if (reason !== 'retry-replacement') {
      hasDetectedRef.current = false;
      detectionInProgressRef.current = false;
    }
    cancelServerProcessingIfNeeded(reason, scope?.requestId);
  }, [cancelServerProcessingIfNeeded]);

  const abortActiveRequestRef = useRef(abortActiveRequest);
  abortActiveRequestRef.current = abortActiveRequest;

  useEffect(() => () => abortActiveRequestRef.current('unmount'), []);

  useEffect(() => {
    const onPageHide = () => {
      if (detectionInProgressRef.current || isDetectingRef.current) {
        cancelServerProcessingIfNeeded('pagehide', activeDetectRequestIdRef.current);
      }
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [cancelServerProcessingIfNeeded]);

  const navigateAfterAnalysis = useCallback(
    (
      skinResults,
      finalSessionId,
      aiConditionDescriptions = null,
      previousSkinData = null,
      macroAnalysis = null,
      visionInsight = null,
      routinePrefetch = null,
      macroPending = false,
      macroSkipped = false,
    ) => {
      const waitingForMacro = Boolean(
        macroPending && !(macroAnalysis?.regions && typeof macroAnalysis.regions === 'object')
      );
      if (waitingForMacro && finalSessionId) {
        try {
          sessionStorage.setItem(`salon-macro-pending:${finalSessionId}`, '1');
        } catch {
          /* ignore */
        }
      }
      navigate(pathWithSessionId('/skin-routine', finalSessionId), {
        state: {
          capturedImage,
          sideCaptures: sideCaptures || undefined,
          userData: userDataFromState,
          sessionId: finalSessionId,
          skinResults,
          aqiData: aqiDataFromState,
          aiConditionDescriptions: aiConditionDescriptions ?? null,
          previousSkinData: previousSkinData ?? null,
          skinGoalData: { goals: [] },
          macroAnalysis: macroAnalysis ?? null,
          macroPending: waitingForMacro,
          macroSkipped: macroSkipped || undefined,
          visionInsight: visionInsight ?? null,
          routineData: routinePrefetch ?? null,
          forceRegenerateForNewScan: !(
            routinePrefetch
            && (
              (typeof routinePrefetch.summary === 'string' && routinePrefetch.summary.trim())
              || (
                Array.isArray(routinePrefetch.routine?.morning) && routinePrefetch.routine.morning.length > 0
              )
              || (
                Array.isArray(routinePrefetch.routine?.evening) && routinePrefetch.routine.evening.length > 0
              )
            )
          ),
          ...(token && { token }),
        },
        replace: true,
      });
    },
    [navigate, capturedImage, sideCaptures, userDataFromState, aqiDataFromState, token]
  );

  const runDetection = useCallback(
    async (imageDataUrl, { isManualRetry = false } = {}) => {
      if (
        !imageDataUrl ||
        !sessionId ||
        hasDetectedRef.current ||
        detectionInProgressRef.current ||
        (isManualRetry && manualRetryBusyRef.current)
      ) {
        return;
      }

      if (isManualRetry) manualRetryBusyRef.current = true;
      abortActiveRequest('retry-replacement');
      const scope = createRequestScope();
      requestScopeRef.current = scope;
      activeDetectRequestIdRef.current = scope.requestId;

      hasDetectedRef.current = true;
      detectionInProgressRef.current = true;
      setIsDetecting(true);
      setError(null);
      setRequestStatus(REQUEST_STATUS.PROCESSING);
      setDetectProgress({ phase: 'starting', message: 'Preparing analysis…', batchSize: 1 });

      const currentSessionId = sessionId ?? location.state?.sessionId ?? getSessionId();
      if (macroSkippedFromState && currentSessionId) {
        try {
          sessionStorage.removeItem(`salon-macro-pending:${currentSessionId}`);
        } catch {
          /* ignore */
        }
      }
      const userAge = userDataFromState?.age ?? null;

      const finishWithError = (message, status = REQUEST_STATUS.RETRY_AVAILABLE) => {
        hasDetectedRef.current = false;
        detectionInProgressRef.current = false;
        setError(message);
        setRequestStatus(status);
        setIsDetecting(false);
      };

      try {
        const [allowedConditions, previousRes] = await Promise.all([
          token
            ? fetchAllowedConditionNames(token).then((allowedSet) => (
              allowedSet ? Array.from(allowedSet) : null
            ))
            : Promise.resolve(null),
          ENABLE_RADAR_CHART && ENABLE_BEFORE_RADAR_CHART
            ? getPreviousSkinAnalysis(currentSessionId, token)
            : Promise.resolve({ success: true, previous: null }),
        ]);

        if (scope.isAborted()) return;

        const hasMacro =
          !macroSkippedFromState
          && macroCapturesFromState
          && typeof macroCapturesFromState === 'object'
          && (macroCapturesFromState.forehead || macroCapturesFromState.cheek);

        // Start skin age/type immediately so VL runs during detect (not after page load).
        // Defer macro VL until after skin-age so they do not contend on the same GPU worker.
        const skinAgePromise = requestSkinAge({
          capturedImage: imageDataUrl,
          sessionId: currentSessionId,
          appSessionToken: token || null,
          aqiData: aqiDataFromState?.success ? {
            aqi: aqiDataFromState.aqi,
            category: aqiDataFromState.category,
            location: aqiDataFromState.location,
            uv_index: aqiDataFromState.uv_index ?? null,
            uv_level: aqiDataFromState.uv_level,
            temperature: aqiDataFromState.temperature ?? null,
            humidity: aqiDataFromState.humidity ?? null,
            wind_speed: aqiDataFromState.wind_speed ?? null,
          } : null,
          signal: scope.signal,
        }).catch((ageErr) => {
          logSalonResilience('skin_age_prefetch_failed', {
            message: ageErr?.message || String(ageErr),
            sessionId: currentSessionId,
          });
          return null;
        });

        const detectPromise = detectSkinBatch({
          frontImage: imageDataUrl,
          sideCaptures: sideCaptures || undefined,
          sessionId: currentSessionId,
          age: userAge,
          allowedConditions,
          appSessionToken: token || null,
          useOllamaForDescriptions: Boolean(ENABLE_OLLAMA_DESCRIPTIONS),
          retryCount: retryCountRef.current,
          signal: scope.signal,
          requestId: scope.requestId,
          onProgress: setDetectProgress,
          onRetry: ({ reconnecting, timeout }) => {
            setRequestStatus(
              reconnecting || timeout ? REQUEST_STATUS.RECONNECTING : REQUEST_STATUS.RETRYING
            );
            setDetectProgress((prev) => ({
              ...(prev || {}),
              phase: 'retry',
              message: reconnecting
                ? 'Reconnecting to server…'
                : CONF_THRESHOLD_MESSAGE,
            }));
          },
        });

        const data = await detectPromise;

        if (scope.isAborted()) return;

        if (!data.success || !data.marked_image) {
          const persistFail = data?.persistence_failed;
          finishWithError(
            data.error || (persistFail ? 'Failed to save analysis results. Please try again.' : 'Detection failed'),
            persistFail
              ? (data?.retryable === false ? REQUEST_STATUS.FAILED : REQUEST_STATUS.RETRY_AVAILABLE)
              : REQUEST_STATUS.FAILED
          );
          return;
        }

        if (data.persistence_verified !== true) {
          finishWithError(
            'Analysis completed but persistence was not verified. Please try again.',
            REQUEST_STATUS.RETRY_AVAILABLE
          );
          return;
        }

        const detectionsList = normalizeDetections(data.detections ?? []);
        const frontDetections = normalizeDetections(data.front_detections ?? data.detections ?? []);
        const finalSkinConditions = data.skin_conditions ?? null;
        const finalSkinScore = normalizeSkinScore(data.skin_score ?? null);
        const hadSessionFromState = !!(sessionId ?? location.state?.sessionId);
        const finalSessionId = hadSessionFromState
          ? currentSessionId
          : (data.session_id || currentSessionId);

        if (data.session_id && !hadSessionFromState) {
          setSessionId(data.session_id);
        }

        setDetectProgress({
          phase: 'persist',
          message: 'Saving analysis…',
          batchSize: 1,
        });

        const saveResult = await saveSkinDetection(
          finalSessionId,
          {
            detections: detectionsList,
            frontDetections,
            sideResults: data.side_results || null,
            skinScore: finalSkinScore,
            skinConditions: finalSkinConditions,
            markedImage: data.marked_image,
          },
          userDataFromState ?? {},
          token ?? null
        );

        if (scope.isAborted()) return;

        if (!saveResult?.success) {
          finishWithError(
            saveResult?.error || 'Failed to save analysis. Please try again.',
            saveResult?.retryable === false ? REQUEST_STATUS.FAILED : REQUEST_STATUS.RETRY_AVAILABLE
          );
          return;
        }

        const aiDescriptions =
          ENABLE_OLLAMA_DESCRIPTIONS
          && data?.ai_condition_descriptions
          && typeof data.ai_condition_descriptions === 'object'
            ? normalizeDescriptionMap(data.ai_condition_descriptions)
            : {};

        const previousSkinData =
          previousRes.success && previousRes.previous ? previousRes.previous : null;

        setDetectProgress({
          phase: 'ai_bundle',
          message: 'Estimating skin age & type…',
          batchSize: 1,
        });

        // Finish age first (light VL) so routine/macro do not steal the GPU mid-estimate.
        const visionInsight = await skinAgePromise;
        if (scope.isAborted()) return;

        const aqiPayload = aqiDataFromState?.success ? {
          aqi: aqiDataFromState.aqi,
          category: aqiDataFromState.category,
          location: aqiDataFromState.location,
          coordinates: aqiDataFromState.coordinates,
          latitude: aqiDataFromState.latitude,
          longitude: aqiDataFromState.longitude,
          uv_index: aqiDataFromState.uv_index ?? null,
          uv_level: aqiDataFromState.uv_level,
          temperature: aqiDataFromState.temperature ?? null,
          humidity: aqiDataFromState.humidity ?? null,
          wind_speed: aqiDataFromState.wind_speed ?? null,
        } : null;

        // Start macro as soon as age is done so it runs alongside routine generation.
        let macroAnalysis = null;
        let macroPromise = null;
        if (hasMacro) {
          try {
            sessionStorage.setItem(`salon-macro-pending:${finalSessionId}`, '1');
          } catch {
            /* ignore */
          }
          setDetectProgress({
            phase: 'routine',
            message: 'Building report & close-up analysis…',
            batchSize: 1,
          });
          macroPromise = analyzeSkinMacro({
            sessionId: finalSessionId,
            macroCaptures: macroCapturesFromState,
            appSessionToken: token || null,
          }).then((macroRes) => {
            const macro = macroRes?.macro || null;
            if (macro) {
              window.dispatchEvent(new CustomEvent('salon-macro-analysis-ready', {
                detail: { sessionId: finalSessionId, macro },
              }));
            }
            return macro;
          }).catch((macroErr) => {
            logSalonResilience('macro_analyze_failed', {
              message: macroErr?.message || String(macroErr),
              sessionId: finalSessionId,
            });
            return null;
          });
        } else {
          setDetectProgress({
            phase: 'routine',
            message: 'Building tips, summary & routine…',
            batchSize: 1,
          });
        }

        const routineRes = await requestGenerateRoutine({
          detections: detectionsList,
          skinScore: finalSkinScore,
          skinConditions: finalSkinConditions,
          userData: userDataFromState ?? {},
          sessionId: finalSessionId,
          appSessionToken: token || null,
          aqiData: aqiPayload,
          previousSkinData,
          useOllamaForSummary: true,
          useOllamaForRoutine: true,
          forceRegenerate: true,
          skipVisionInsight: true,
          signal: scope.signal,
        }).catch((routineErr) => {
          logSalonResilience('routine_prefetch_failed', {
            message: routineErr?.message || String(routineErr),
            sessionId: finalSessionId,
          });
          return null;
        });
        if (scope.isAborted()) return;

        // Keep the analysis loader up until macro is ready (or fails), then open report.
        if (macroPromise) {
          setDetectProgress({
            phase: 'macro',
            message: 'Analyzing close-up microscope images…',
            batchSize: 1,
          });
          macroAnalysis = await macroPromise;
          if (scope.isAborted()) return;
          if (!macroAnalysis?.regions) {
            try {
              sessionStorage.removeItem(`salon-macro-pending:${finalSessionId}`);
            } catch {
              /* ignore */
            }
          }
        }

        let routinePrefetch = null;
        const routineSummary =
          routineRes?.success && typeof routineRes.summary === 'string' ? routineRes.summary.trim() : '';
        const routineHasSteps = Boolean(
          routineRes?.success
          && routineRes.routine
          && (
            (Array.isArray(routineRes.routine.morning) && routineRes.routine.morning.length > 0)
            || (Array.isArray(routineRes.routine.evening) && routineRes.routine.evening.length > 0)
          ),
        );
        if (routineRes?.success && (routineSummary || routineHasSteps)) {
          routinePrefetch = {
            summary: routineSummary,
            routine: routineRes.routine || { morning: [], evening: [] },
            tips: Array.isArray(routineRes.tips) ? routineRes.tips : (routineRes.routine?.tips || []),
            insight: routineRes.insight || routineRes.routine?.insight || '',
            cacheVersion: 2,
          };
        }
        if (visionInsight) {
          if (!routinePrefetch) {
            routinePrefetch = {
              summary: '',
              routine: { morning: [], evening: [] },
              tips: [],
              insight: '',
              cacheVersion: 2,
            };
          }
          routinePrefetch.skinAge = {
            skin_age: visionInsight.estimated_age ?? visionInsight.skin_age,
            estimated_age: visionInsight.estimated_age ?? visionInsight.skin_age,
            type: visionInsight.type || 'visual_estimate',
            confidence: visionInsight.confidence,
            explanation: visionInsight.explanation,
          };
          if (visionInsight.photo_insight) {
            routinePrefetch.photoInsight = visionInsight.photo_insight;
          }
          if (visionInsight.env_tip) {
            routinePrefetch.envTip = visionInsight.env_tip;
          }
        }

        setDetectProgress({
          phase: 'complete',
          message: 'Opening your report…',
          batchSize: 1,
        });
        await new Promise((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });

        navigateAfterAnalysis(
          {
            detections: detectionsList,
            frontDetections,
            skinScore: finalSkinScore,
            skinConditions: finalSkinConditions,
            markedImage: data.marked_image,
            faceShapes: data.face_shapes || null,
            sideResults: data.side_results || null,
            aiConditionDescriptionsPending: Boolean(data.ai_condition_descriptions_pending),
            capturedImage: imageDataUrl,
          },
          finalSessionId,
          aiDescriptions,
          previousSkinData,
          macroAnalysis,
          visionInsight,
          routinePrefetch,
          Boolean(hasMacro && !(macroAnalysis?.regions)),
          macroSkippedFromState,
        );
      } catch (err) {
        if (isAbortError(err)) return;

        if (err.status === 409) {
          finishWithError(err.apiResponse?.error || CONF_THRESHOLD_MESSAGE, REQUEST_STATUS.QUEUED);
          return;
        }
        if (isTimeoutError(err)) {
          finishWithError(
            'Analysis timed out. The server may still be processing — try again in a moment.',
            REQUEST_STATUS.TIMEOUT
          );
          return;
        }
        if (err.status === 503 || err.retryable) {
          finishWithError(
            err.apiResponse?.error || err.message || 'Processing interrupted. Please try again.',
            REQUEST_STATUS.RETRY_AVAILABLE
          );
          return;
        }
        finishWithError(err.message || 'Failed to detect skin conditions', REQUEST_STATUS.FAILED);
      } finally {
        if (requestScopeRef.current === scope) {
          requestScopeRef.current = null;
        }
        if (activeDetectRequestIdRef.current === scope.requestId) {
          activeDetectRequestIdRef.current = null;
        }
        detectionInProgressRef.current = false;
        setIsDetecting(false);
        manualRetryBusyRef.current = false;
      }
    },
    [
      sessionId,
      setSessionId,
      location.state?.sessionId,
      userDataFromState,
      aqiDataFromState,
      token,
      sideCaptures,
      macroCapturesFromState,
      macroSkippedFromState,
      ENABLE_OLLAMA_DESCRIPTIONS,
      ENABLE_RADAR_CHART,
      ENABLE_BEFORE_RADAR_CHART,
      navigateAfterAnalysis,
      abortActiveRequest,
    ]
  );

  const runDetectionRef = useRef(runDetection);
  runDetectionRef.current = runDetection;

  useEffect(() => {
    if (
      capturedImage &&
      sessionId &&
      !hasDetectedRef.current &&
      !detectionInProgressRef.current
    ) {
      runDetectionRef.current(capturedImage);
    }
  }, [capturedImage, sessionId]);

  const handleBack = useCallback(() => {
    abortActiveRequest('route-change');
    const sid = sessionId ?? getSessionIdFromUrl(location) ?? getSessionId();
    navigate(pathWithSessionId('/skin-macro', sid), {
      state: {
        capturedImage,
        userData: userDataFromState,
        sessionId,
        aqiData: aqiDataFromState,
        ...(token && { token }),
      },
    });
  }, [navigate, sessionId, location, capturedImage, userDataFromState, aqiDataFromState, token, abortActiveRequest]);

  const handleHome = useCallback(() => {
    abortActiveRequest('route-change');
    goSalonHubForNewUser(navigate, {
      sessionId,
      token: token || null,
    });
  }, [navigate, sessionId, token, abortActiveRequest]);

  const handleRetry = useCallback(() => {
    if (manualRetryBusyRef.current || detectionInProgressRef.current) return;
    retryCountRef.current += 1;
    hasDetectedRef.current = false;
    runDetection(capturedImage, { isManualRetry: true });
  }, [runDetection, capturedImage]);

  const loaderMessage = detectProgress?.message || 'Analyzing your skin…';
  const showLoader = !error && (isDetecting || requestStatus === REQUEST_STATUS.PROCESSING);

  const renderPageHeader = () => (
    <header className="ai-skin-analysis-page-nav" role="banner">
      <button
        type="button"
        className="ai-skin-analysis-page-back-button"
        onClick={handleBack}
        aria-label="Go back"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      <div className="ai-skin-analysis-page-brand">
        <FaceIdentityBrandLogo
          showLogo={showLogo}
          orgLogoUrl={orgLogoUrl}
          onLogoError={onLogoError}
          className="ai-skin-analysis-page-brand-logo"
          imgClassName="ai-skin-analysis-page-brand-logo-img"
          fallbackClassName="ai-skin-analysis-page-brand-fallback"
          alt="Naturals"
        />
      </div>
      <div className="ai-skin-analysis-page-nav-right">
        <span className="ai-skin-analysis-safe-badge">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M12 22s8-4 8-10V6l-8-4-8 4v6c0 6 8 10 8 10z" />
            <path d="M9.5 12.2l1.8 1.8 3.7-3.9" />
          </svg>
          Your data is safe
        </span>
        <SalonHeaderActions homeButtonClassName="ai-skin-analysis-page-home-button" onHome={handleHome} />
      </div>
    </header>
  );

  const renderPageIntro = () => (
    <div className="ai-skin-analysis-page-intro">
      <h1 className="ai-skin-analysis-page-title skin-text-h1">Skin Analysis</h1>
      <p className="ai-skin-analysis-page-subtitle skin-text-body">Advanced AI is analyzing your skin</p>
    </div>
  );

  if (!capturedImage) {
    return (
      <div className="ai-skin-analysis-page page-bg">
        {renderPageHeader()}
        <main className="ai-skin-analysis-page-main">
          <div className="error-container">
            <h2>No Image Found</h2>
            <p>Please capture an image first.</p>
            <button type="button" className="skin-action-button" onClick={handleBack}>
              Go Back
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="ai-skin-analysis-page page-bg">
      {renderPageHeader()}
      <main className="ai-skin-analysis-page-main">
        {showLoader ? renderPageIntro() : null}
        {error ? (
          <div className="error-container">
            <h2>
              {requestStatus === REQUEST_STATUS.TIMEOUT
                ? 'Timed Out'
                : requestStatus === REQUEST_STATUS.QUEUED
                  ? 'Please Wait'
                  : 'Error'}
            </h2>
            <p>{error}</p>
            <button type="button" className="skin-action-button" onClick={handleRetry}>
              Try Again
            </button>
          </div>
        ) : showLoader ? (
          <AISkinLoader
            imageSrc={capturedImage}
            progressMessage={loaderMessage}
            detectProgress={detectProgress}
          />
        ) : null}
      </main>
    </div>
  );
};

export default AISkinAnalysisPage;
